import { cn } from '@frontend-platform/ui-lib';
import { DropdownMenuCheckboxItem, DropdownMenuSeparator } from '@frontend-platform/ui-lib/ui';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { BeforeSaveDevBot } from './dev-bot-storage';
import { type DevSessionSyncResult, syncDevSession } from './dev-session';
import { DevShellBackendBadge } from './dev-shell-backend-badge';
import { DevShellBotConfig } from './dev-shell-bot-config';
import {
  DevShellEnvToggle,
  type DevShellEnvToggleConfig,
  type DevShellMockApi,
  DevShellMockToggle,
} from './dev-shell-controls';
import { useDevShellChromeHidden } from './dev-shell-fullscreen';
import { DevShellHeader } from './dev-shell-header';
import { type DevShellPreview, readPreview, storePreview } from './dev-shell-preview';
import { type DevShellLinkRenderer, DevShellRail, DevShellSidebar } from './dev-shell-sidebar';
import { DevShellUserMenu } from './dev-shell-user-menu';
import type { SidebarNode } from './types';

export interface DevShellConfig {
  /** Nx project name; keys the persisted navigation state and the badge's restart commands. */
  projectName: string;
  /**
   * Each dev-setup control below is shown in every app. One the app has not wired renders
   * disabled, with a tooltip that says what to add.
   */
  /** The app's `VITE_<APP>_LOCAL_ENV` value; enables the backend badge. */
  localEnv?: string;
  /** A cheap same-origin GET through the dev proxy; enables the badge's reachability dot. */
  healthCheckPath?: string;
  /** Enables the Non-EP / EP toggle for apps that read `currentBot.envProductType`. */
  envToggle?: DevShellEnvToggleConfig;
  /** Enables the Mock toggle for apps with a mock/real API dispatcher. */
  mockApi?: DevShellMockApi;
  onBeforeSaveBot?: BeforeSaveDevBot;
  /** App-specific localStorage keys that Reset dev state also clears. */
  resetKeys?: string[];
}

export interface DevShellProps {
  config: DevShellConfig;
  sidebar: SidebarNode[];
  /** App-specific dev controls, rendered after the standard ones and before the bot dialog trigger. */
  headerActions?: ReactNode;
  /** Renders `children` alone: production builds and host-mounted (embedded or federated) runs. */
  disabled?: boolean;
  /** Router-aware apps pass the router's pathname; defaults to `window.location.pathname`. */
  pathname?: string;
  /** Router-aware apps pass their `<Link>`; defaults to a plain `<a href>`. */
  renderLink?: DevShellLinkRenderer;
  children: ReactNode;
}

function readNavigationOpen(storageKey: string) {
  try {
    return localStorage.getItem(storageKey) !== 'false';
  } catch {
    return true;
  }
}

function storeNavigationOpen(storageKey: string, isOpen: boolean) {
  try {
    localStorage.setItem(storageKey, String(isOpen));
  } catch {
    // Navigation still toggles for this session when storage is unavailable.
  }
}

export function DevShell({
  config,
  sidebar,
  headerActions,
  disabled = false,
  pathname,
  renderLink,
  children,
}: DevShellProps) {
  const storageKey = `${config.projectName}.dev-shell.navigation-open`;
  const navigationId = `${config.projectName}-dev-navigation`;
  const [isNavigationOpen, setIsNavigationOpen] = useState(() => readNavigationOpen(storageKey));
  const chromeHidden = useDevShellChromeHidden();
  const [preview, setPreview] = useState(() => readPreview(config.projectName));
  const [isMockEnabled, setIsMockEnabled] = useState(() => config.mockApi?.isEnabled() ?? false);

  // StrictMode mounts twice in dev; one sync per instance lets the surviving mount reload.
  const sessionSync = useRef<Promise<DevSessionSyncResult> | null>(null);
  useEffect(() => {
    if (disabled) return;
    let cancelled = false;
    sessionSync.current ??= syncDevSession();
    sessionSync.current.then((result) => {
      if (result === 'applied' && !cancelled) window.location.reload();
    });
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  if (disabled) {
    return <>{children}</>;
  }

  if (chromeHidden) {
    return (
      <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-white">{children}</div>
    );
  }

  function togglePreview(key: keyof DevShellPreview) {
    setPreview((current) => {
      const next = { ...current, [key]: !current[key] };
      storePreview(config.projectName, next);
      return next;
    });
  }

  function toggleNavigation() {
    setIsNavigationOpen((current) => {
      const next = !current;
      storeNavigationOpen(storageKey, next);
      return next;
    });
  }

  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-white">
      <DevShellHeader
        className={cn(preview.cortexPanel && 'pr-126')}
        navigationId={navigationId}
        isNavigationOpen={isNavigationOpen}
        onToggleNavigation={toggleNavigation}
        actions={
          <>
            <DevShellBackendBadge
              projectName={config.projectName}
              localEnv={config.localEnv}
              healthCheckPath={config.healthCheckPath}
              mockActive={isMockEnabled}
            />
            <DevShellEnvToggle config={config.envToggle} />
            <DevShellMockToggle mockApi={config.mockApi} onChange={setIsMockEnabled} />
            {headerActions}
            <DevShellBotConfig onBeforeSaveBot={config.onBeforeSaveBot} />
            <DevShellUserMenu projectName={config.projectName} resetKeys={config.resetKeys}>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={preview.hostPadding}
                onCheckedChange={() => togglePreview('hostPadding')}
                onSelect={(event) => event.preventDefault()}
              >
                Host content padding (32 / 24 / 24)
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={preview.cortexPanel}
                onCheckedChange={() => togglePreview('cortexPanel')}
                onSelect={(event) => event.preventDefault()}
              >
                Cortex panel open (504px)
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
            </DevShellUserMenu>
          </>
        }
      />
      <div className={cn('flex min-h-0 flex-1 overflow-hidden', preview.cortexPanel && 'pr-126')}>
        {isNavigationOpen ? (
          <DevShellSidebar
            id={navigationId}
            nodes={sidebar}
            pathname={pathname ?? window.location.pathname}
            renderLink={renderLink}
            onBeforeSaveBot={config.onBeforeSaveBot}
          />
        ) : (
          <DevShellRail
            id={navigationId}
            nodes={sidebar}
            pathname={pathname ?? window.location.pathname}
            renderLink={renderLink}
          />
        )}
        <div
          data-host-padding={preview.hostPadding || undefined}
          className={cn(
            'flex min-w-0 flex-1 flex-col overflow-auto bg-white',
            preview.hostPadding && 'px-6 pt-8 pb-6'
          )}
        >
          {children}
        </div>
      </div>
      {preview.cortexPanel ? (
        <aside
          aria-label="Cortex panel preview"
          className="fixed top-0 right-0 flex h-screen w-126 flex-col items-center justify-center gap-1 border-steel-blue-45 border-l bg-steel-blue-5 text-center"
        >
          <span className="font-semibold text-sm text-steel-dark">Cortex panel</span>
          <span className="max-w-80 text-steel-light text-xs">
            Preview of the 504px space aistudio reserves when the Cortex panel is open.
          </span>
        </aside>
      ) : null}
    </div>
  );
}
